// src/services/authService.js
import api from "./api";
import { clearAllowedOrgsCache } from "@/utils/workspaceOrganisations";
import { clearWorkspaceOrgCache } from "@/utils/workspaceOrgFilter";

export const login = async (username, password) => {
  try {
    const response = await api.post("/token/", {
      username,
      password,
    });

    // Get the JWT token from the response
    const token = response.data.access;

    // Save the token to localStorage
    localStorage.setItem("token", token);
    // Persist the refresh token so refreshToken() can renew the (now short-lived,
    // 1h) access token instead of forcing a re-login when it expires. Clear any
    // stale token if this login response did not include one.
    if (response.data.refresh) {
      localStorage.setItem("refresh_token", response.data.refresh);
    } else {
      localStorage.removeItem("refresh_token");
    }

    // Update the api instance authorization header
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    return token;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error("Invalid username or password.");
    } else if (error.response?.status === 500) {
      throw new Error("Server error. Please try again later.");
    } else {
      throw new Error(
        "Login failed. Please check your credentials and try again."
      );
    }
  }
};

// You might want to add these additional auth-related functions:
export const logout = () => {
  clearAllowedOrgsCache();
  clearWorkspaceOrgCache();
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  delete api.defaults.headers.common["Authorization"];
};

export const refreshToken = async () => {
  try {
    const refresh = localStorage.getItem("refresh_token");
    if (!refresh) throw new Error("No refresh token available");

    const response = await api.post("/token/refresh/", {
      refresh: refresh,
    });

    const token = response.data.access;
    localStorage.setItem("token", token);
    // With ROTATE_REFRESH_TOKENS the backend returns a NEW refresh token and
    // blacklists the consumed one, so the rotated token must be persisted or the
    // next refresh would reuse a blacklisted token and fail.
    if (response.data.refresh) {
      localStorage.setItem("refresh_token", response.data.refresh);
    }
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    return token;
  } catch (error) {
    logout();
    throw new Error("Session expired. Please login again.");
  }
};

// Optional: function to check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem("token");
};

// Password reset request - sends email with reset link
export const requestPasswordReset = async (username) => {
  try {
    const response = await api.post("/password_reset/", { email: username });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No account found with this username.");
    } else if (error.response?.status === 500) {
      throw new Error("Server error. Please try again later.");
    } else {
      throw new Error("Failed to send password reset email. Please try again.");
    }
  }
};

// Reset password with token
export const resetPassword = async (uidb64, token, newPassword) => {
  try {
    const response = await api.post(`/reset/${uidb64}/${token}/`, {
      new_password1: newPassword,
      new_password2: newPassword,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data.detail || "Password reset failed. Please try again.");
    } else if (error.response?.status === 500) {
      throw new Error("Server error. Please try again later.");
    } else {
      throw new Error("Failed to reset password. Please try again.");
    }
  }
};

// Verify password reset token
export const verifyResetToken = async (uidb64,token) => {
  try {
    const response = await api.get(`/reset/${uidb64}/${token}/verify/`);
    return response.data;
  } catch (error) {
    throw new Error("Invalid or expired password reset link.");
  }
};


export const setPassword = async (id,data) => {
  try {
    const response = await api.post(`/password-change/${id}/user/`, data);
    return response.data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};
